import {testIdProps} from '@github-ui/test-id-props'
import {GearIcon, PauseIcon, PlayIcon, ZapIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Label} from '@primer/react'
import {clsx} from 'clsx'

import type {ClientMemexWorkflow} from '../../api/workflows/contracts'
import {AutoAssignAgentColumnWorkflowDefinition, type ColumnWorkflowState} from '../../helpers/workflow-utilities'
import {WorkflowResources} from '../../strings'
import styles from './column-workflow-indicator.module.css'

type ColumnWorkflowIndicatorProps = {
  columnName: string
  workflowStates: ReadonlyArray<ColumnWorkflowState>
  onConfigureWorkflows: () => void
  onOpenWorkflow: (workflow: ClientMemexWorkflow) => void
  onToggleWorkflowEnabled: (workflow: ClientMemexWorkflow, enabled: boolean) => void
}

type ColumnWorkflowStatusLabelProps = {
  workflowStates: ReadonlyArray<ColumnWorkflowState>
}

function columnWorkflowAriaLabel(columnName: string, workflowStates: ReadonlyArray<ColumnWorkflowState>) {
  const firstWorkflowState = workflowStates[0]
  if (workflowStates.length === 1 && firstWorkflowState) {
    if (firstWorkflowState.definition === AutoAssignAgentColumnWorkflowDefinition) {
      return firstWorkflowState.enabled
        ? WorkflowResources.assignCopilotColumnIndicatorEnabledAriaLabel(columnName)
        : WorkflowResources.assignCopilotColumnIndicatorDisabledAriaLabel(columnName)
    }

    return firstWorkflowState.enabled
      ? WorkflowResources.columnWorkflowIndicatorEnabled(columnName, firstWorkflowState.workflow.name)
      : WorkflowResources.columnWorkflowIndicatorDisabled(columnName, firstWorkflowState.workflow.name)
  }

  return WorkflowResources.columnWorkflowsIndicator(columnName, workflowStates.length)
}

function columnWorkflowTestId(workflowStates: ReadonlyArray<ColumnWorkflowState>) {
  return workflowStates.length === 1 && workflowStates[0]?.definition === AutoAssignAgentColumnWorkflowDefinition
    ? 'auto-assign-workflow-column-indicator'
    : 'column-workflow-indicator'
}

export function ColumnWorkflowStatusLabel({workflowStates}: ColumnWorkflowStatusLabelProps) {
  if (workflowStates.length === 0) return null

  const hasEnabledWorkflow = workflowStates.some(workflowState => workflowState.enabled)
  const statusLabelClassName = clsx(
    styles.ColumnWorkflowStatusLabel,
    !hasEnabledWorkflow && styles.ColumnWorkflowStatusLabelDisabled,
  )

  return (
    <Label className={statusLabelClassName} {...testIdProps('column-workflow-status-label')}>
      {WorkflowResources.assignCopilotColumnStatusLabel}
    </Label>
  )
}

export function ColumnWorkflowIndicator({
  columnName,
  workflowStates,
  onConfigureWorkflows,
  onOpenWorkflow,
  onToggleWorkflowEnabled,
}: ColumnWorkflowIndicatorProps) {
  if (workflowStates.length === 0) return null

  const hasEnabledWorkflow = workflowStates.some(workflowState => workflowState.enabled)
  const ariaLabel = columnWorkflowAriaLabel(columnName, workflowStates)
  const indicatorClassName = clsx(
    styles.ColumnWorkflowIndicator,
    !hasEnabledWorkflow && styles.ColumnWorkflowIndicatorDisabled,
  )

  // Currently we only support the auto-assign workflow.
  const autoAssignWorkflowState =
    workflowStates.length === 1 && workflowStates[0]?.definition === AutoAssignAgentColumnWorkflowDefinition
      ? workflowStates[0]
      : undefined
  const autoAssignWorkflowStatusClassName = autoAssignWorkflowState
    ? clsx(
        styles.WorkflowStatusPill,
        autoAssignWorkflowState.enabled ? styles.WorkflowStatusPillActive : styles.WorkflowStatusPillPaused,
      )
    : undefined
  const tooltipDescription = autoAssignWorkflowState
    ? autoAssignWorkflowState.enabled
      ? WorkflowResources.assignCopilotColumnIndicatorEnabled
      : WorkflowResources.assignCopilotColumnIndicatorDisabled
    : undefined

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={ZapIcon}
          variant="invisible"
          size="small"
          aria-label={ariaLabel}
          description={tooltipDescription}
          className={indicatorClassName}
          {...testIdProps(columnWorkflowTestId(workflowStates))}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        <ActionList>
          <ActionList.Group>
            <ActionList.GroupHeading className={styles.GroupHeading}>
              <span className={styles.GroupHeadingRow}>
                <span>{WorkflowResources.workflowsForColumn(columnName)}</span>
                {autoAssignWorkflowState && (
                  <span className={autoAssignWorkflowStatusClassName}>
                    {autoAssignWorkflowState.enabled
                      ? WorkflowResources.workflowActive
                      : WorkflowResources.workflowPaused}
                  </span>
                )}
              </span>
            </ActionList.GroupHeading>
            {autoAssignWorkflowState ? (
              <>
                <ActionList.Item onSelect={() => onOpenWorkflow(autoAssignWorkflowState.workflow)}>
                  <ActionList.LeadingVisual>
                    <span
                      className={clsx(
                        styles.WorkflowStateDot,
                        !autoAssignWorkflowState.enabled && styles.WorkflowStateDotDisabled,
                      )}
                    />
                  </ActionList.LeadingVisual>
                  <span>{autoAssignWorkflowState.workflow.name}</span>
                  <ActionList.Description variant="block">
                    {autoAssignWorkflowState.enabled ? WorkflowResources.workflowOn : WorkflowResources.workflowOff}
                  </ActionList.Description>
                </ActionList.Item>
                <ActionList.Divider />
                <ActionList.Item
                  onSelect={() =>
                    onToggleWorkflowEnabled(autoAssignWorkflowState.workflow, !autoAssignWorkflowState.enabled)
                  }
                  aria-label={
                    autoAssignWorkflowState.enabled
                      ? WorkflowResources.pauseAutoAssignWorkflowAriaLabel
                      : WorkflowResources.enableAutoAssignWorkflowAriaLabel
                  }
                  {...testIdProps('auto-assign-workflow-toggle-menu-item')}
                >
                  <ActionList.LeadingVisual>
                    {autoAssignWorkflowState.enabled ? <PauseIcon /> : <PlayIcon />}
                  </ActionList.LeadingVisual>
                  {autoAssignWorkflowState.enabled
                    ? WorkflowResources.pauseAutoAssignWorkflow
                    : WorkflowResources.enableAutoAssignWorkflow}
                </ActionList.Item>
                <ActionList.Item onSelect={onConfigureWorkflows}>
                  <ActionList.LeadingVisual>
                    <GearIcon />
                  </ActionList.LeadingVisual>
                  {WorkflowResources.configureWorkflows}
                </ActionList.Item>
              </>
            ) : (
              workflowStates.map(workflowState => (
                <ActionList.Item
                  key={workflowState.workflow.clientId}
                  onSelect={() => onOpenWorkflow(workflowState.workflow)}
                  {...testIdProps('column-workflow-indicator-item')}
                >
                  <ActionList.LeadingVisual>
                    <span
                      className={clsx(
                        styles.WorkflowStateDot,
                        !workflowState.enabled && styles.WorkflowStateDotDisabled,
                      )}
                    />
                  </ActionList.LeadingVisual>
                  {workflowState.workflow.name}
                  <ActionList.Description variant="block">
                    {workflowState.enabled ? WorkflowResources.workflowOn : WorkflowResources.workflowOff}
                  </ActionList.Description>
                </ActionList.Item>
              ))
            )}
          </ActionList.Group>
          {!autoAssignWorkflowState && (
            <>
              <ActionList.Divider />
              <ActionList.Item onSelect={onConfigureWorkflows}>
                <ActionList.LeadingVisual>
                  <GearIcon />
                </ActionList.LeadingVisual>
                {WorkflowResources.configureWorkflows}
              </ActionList.Item>
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
